import { useEffect, useState } from "react";

type GlobalLoadingProps = {
  label?: string;
  /** Use a real completion percentage when the calling flow can provide one. */
  progress?: number;
  visible?: boolean;
};

function getBoundedProgress(progress: number) {
  return Math.min(100, Math.max(0, Math.round(progress)));
}

export function GlobalLoading({
  label = "Loading Harness",
  progress,
  visible = true,
}: GlobalLoadingProps) {
  const [estimatedProgress, setEstimatedProgress] = useState(12);

  useEffect(() => {
    if (!visible || progress !== undefined) return;

    setEstimatedProgress(12);
    const intervalId = window.setInterval(() => {
      setEstimatedProgress((current) =>
        Math.min(92, current + Math.max(1, Math.round((94 - current) * 0.08))),
      );
    }, 280);

    return () => window.clearInterval(intervalId);
  }, [progress, visible]);

  if (!visible) return null;

  const displayedProgress = getBoundedProgress(progress ?? estimatedProgress);

  return (
    <div
      className="harness-global-loading"
      role="progressbar"
      aria-live="polite"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={displayedProgress}
      aria-valuetext={`${label}: ${displayedProgress}%`}
    >
      <div
        className="harness-loading-progress"
        aria-hidden="true"
        style={{ width: `${displayedProgress}%` }}
      />
      <span className="sr-only">
        {label}: {displayedProgress}%
      </span>
    </div>
  );
}
